import React, { useState, useEffect, KeyboardEvent } from 'react';
import { Save, ArrowLeft, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { reportUiError } from '../../sentry';
import { useAuth } from '../../context/AuthContext';

interface Newsletter {
  id: number;
  subject: string;
  from: string;
  text?: string;
  html?: string;
  message_id?: string;
  received_at?: string;
  created_at: string;
  status: string;
}

interface NewsletterModalProps {
  isOpen: boolean;
  onClose: () => void;
  newsletter: Newsletter | null;
  onSuccess: () => void;
}

const NewsletterModal: React.FC<NewsletterModalProps> = ({
  isOpen,
  onClose,
  newsletter,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<Newsletter>>({
    subject: '',
    from: '',
    text: '',
    status: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isNewNewsletter = !newsletter;

  useEffect(() => {
    if (newsletter) {
      setFormData({
        subject: newsletter.subject || '',
        from: newsletter.from || '',
        text: newsletter.text || '',
        status: newsletter.status || '',
      });
    } else {
      setFormData({
        subject: '',
        from: '',
        text: '',
        status: '',
      });
    }
  }, [newsletter, isOpen]);

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey as any);
    return () => {
      document.removeEventListener('keydown', handleEscKey as any);
    };
  }, [isOpen, onClose, showDeleteConfirm]);

  const handleDelete = async () => {
    if (!newsletter) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('outsideNewsletters')
        .delete()
        .eq('id', newsletter.id);

      if (error) throw error;

      toast.success('Newsletter deleted successfully');
      onSuccess();
      onClose();
    } catch (error: unknown) {
      reportUiError('outsideNewsletters', 'delete', error, {
        newsletter_id: String(newsletter.id),
      });
      toast.error(`Error deleting newsletter: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.subject?.trim()) {
      toast.error('Subject is required');
      return;
    }

    if (!formData.from?.trim()) {
      toast.error('From is required');
      return;
    }

    setIsSaving(true);

    try {
      const dataToSubmit = {
        subject: formData.subject.trim(),
        from: formData.from.trim(),
        text: formData.text?.trim() || '',
        status: formData.status || null,
      };

      let result;
      if (isNewNewsletter) {
        result = await supabase.from('outsideNewsletters').insert(dataToSubmit).select();
      } else {
        result = await supabase
          .from('outsideNewsletters')
          .update(dataToSubmit)
          .eq('id', newsletter.id)
          .select();
      }

      if (result.error) throw result.error;

      toast.success(isNewNewsletter ? 'Newsletter created successfully' : 'Newsletter updated successfully');
      onSuccess();
      onClose();
    } catch (error: unknown) {
      reportUiError(
        'outsideNewsletters',
        isNewNewsletter ? 'create' : 'update',
        error,
        newsletter ? { newsletter_id: String(newsletter.id) } : undefined,
      );
      toast.error(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-y-0 right-0 w-full md:w-1/2 bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } z-50`}
    >
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold">
              {isNewNewsletter ? 'Create New Newsletter' : 'View Newsletter'}
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                placeholder="Enter subject..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                From <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.from}
                onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                placeholder="Enter sender..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={formData.status || ''}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
              >
                <option value="">None</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Content
              </label>
              <textarea
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                rows={15}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3 font-mono text-sm"
                placeholder="Newsletter content..."
              />
            </div>

            {!isNewNewsletter && !showDeleteConfirm && (
              <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center justify-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Newsletter
                </button>
              </div>
            )}

            {showDeleteConfirm && (
              <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                    Are you sure you want to delete this newsletter? This action cannot be undone.
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        <div className="shrink-0 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving || showDeleteConfirm}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg py-2.5 px-4 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                {isNewNewsletter ? 'Create Newsletter' : 'Update Newsletter'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsletterModal;
